import { useCallback, useEffect, useRef, useState } from 'react'

function readFromStorage<T extends object>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key)

    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as T
    const hasValue = Object.values(parsed).some((value) => {
      if (value === null || value === undefined || value === '') {
        return false
      }

      if (Array.isArray(value)) {
        return value.length > 0
      }

      return true
    })

    return hasValue ? parsed : null
  } catch {
    return null
  }
}

interface Options {
  storageKey: string
  fileFields?: string[]
}

interface Result<T extends object> {
  savedValues: T | null
  onValuesChange: (values: T) => void
  clearStorage: () => void
}

export function useStepFormStorage<T extends object>({
  storageKey,
  fileFields = [],
}: Options): Result<T> {
  const sessionKey = `step_form_${storageKey}`

  const [savedValues, setSavedValues] = useState<T | null>(() => readFromStorage<T>(sessionKey))

  const prevKeyRef = useRef<string>(sessionKey)
  const prevSerializedRef = useRef<string>('')
  const isClearedRef = useRef<boolean>(false)

  useEffect(() => {
    if (prevKeyRef.current === sessionKey) {
      return
    }

    prevKeyRef.current = sessionKey
    prevSerializedRef.current = ''
    isClearedRef.current = false
    setSavedValues(readFromStorage<T>(sessionKey))
  }, [sessionKey])

  const onValuesChange = useCallback(
    (values: T) => {
      if (isClearedRef.current) {
        return
      }

      const scalarValues: Record<string, unknown> = {}
      const record = values as Record<string, unknown>

      for (const key of Object.keys(record)) {
        if (!fileFields.includes(key) && !(record[key] instanceof File)) {
          scalarValues[key] = record[key]
        }
      }

      const serialized = JSON.stringify(scalarValues)

      if (serialized !== prevSerializedRef.current) {
        prevSerializedRef.current = serialized
        sessionStorage.setItem(sessionKey, serialized)
      }
    },
    [sessionKey, fileFields],
  )

  const clearStorage = useCallback(() => {
    sessionStorage.removeItem(sessionKey)
    prevSerializedRef.current = ''
    isClearedRef.current = true
    setSavedValues(null)
  }, [sessionKey])

  return { savedValues, onValuesChange, clearStorage }
}
